import { cva, cx, VariantProps } from "cva";
import { Avatar, Headline } from "~/ui-library";

const LandingPageContainerStyles = cva("relative w-full mx-auto", {
  variants: {
    shouldShowBlockSkeleton: {
      true: "mb-l border-4 border-t-0 border-dashed border-brand",
      false: "",
    },
    hasSidecar: {
      true: "md:flex md:justify-center",
      false: "",
    },
    topPadding: {
      none: "pt-0",
      extraSmall: "pt-xs",
      small: "pt-s",
      medium: "pt-s lg:pt-m",
      large: "pt-m lg:pt-l",
      extraLarge: "pt-l lg:pt-xl",
      xxLarge: "pt-xl lg:pt-xxl",
    },
    bottomPadding: {
      none: "pb-0",
      extraSmall: "pb-xs",
      small: "pb-s",
      medium: "pb-s lg:pb-m",
      large: "pb-m lg:pb-l",
      extraLarge: "pb-l lg:pb-xl",
      xxLarge: "pb-xl lg:pb-xxl",
    },
  },
  defaultVariants: {
    topPadding: "extraLarge",
    bottomPadding: "extraLarge",
  },
});

type SkeletonBlockProps = {
  shouldShowBlockSkeleton?: boolean;
  blockInfo?: {
    blockType: string;
    blockIndex: string | number;
  };
};

export type LandingPageContainerProps<T extends React.ElementType> = {
  as?: T;
  background?: string;
  children: React.ReactNode;
  className?: string;
  sidecar?: React.ReactNode;
} & SkeletonBlockProps &
  VariantProps<typeof LandingPageContainerStyles> &
  React.ComponentPropsWithoutRef<T>;

const SkeletonBlockHeader: React.FC<SkeletonBlockProps> = ({
  blockInfo,
  shouldShowBlockSkeleton,
}) =>
  shouldShowBlockSkeleton ? (
    <div className="flex items-center justify-center border-4 border-b-0 border-dashed border-brand py-m">
      <Avatar>{blockInfo?.blockIndex}</Avatar>
      <Headline className="ml-s" level={0} as="div">
        {blockInfo?.blockType}
      </Headline>
    </div>
  ) : null;

export const LandingPageContainer = <T extends React.ElementType = "div">({
  as,
  background,
  children,
  className,
  topPadding,
  bottomPadding,
  blockInfo,
  shouldShowBlockSkeleton,
  sidecar,
}: LandingPageContainerProps<T>) => {
  const Component = as || "div";
  const allowedBackground = background?.toLowerCase().includes("url")
    ? undefined
    : background;

  const hasSidecar = Boolean(sidecar);

  return (
    <>
      <SkeletonBlockHeader
        blockInfo={blockInfo}
        shouldShowBlockSkeleton={shouldShowBlockSkeleton}
      />
      <Component
        className={cx(
          LandingPageContainerStyles({
            shouldShowBlockSkeleton,
            topPadding,
            bottomPadding,
            hasSidecar,
          }),
          className
        )}
        data-testid="landing-page-container"
        style={{ background: allowedBackground }}
      >
        <div>{children}</div>
        {!!sidecar && <div className="pr-xs md:pr-s lg:pr-m">{sidecar}</div>}
      </Component>
    </>
  );
};
